import type { UploadFile } from "antd";
import React, { useEffect, useState, type ReactNode } from "react";
import { useCurrentUser } from "../../lib/hooks/basic/users";

export interface AgentWidgetType {
  content: ReactNode;
  agent: string;
  onAction(action: string, payload: any, attachments: UploadFile[]): ReactNode;
}

interface AgentContextType {
  agentAvailable: boolean;
  agentVisible: boolean;
  agentSidebarWidth: number;
  setAgentSidebarWidth: (width: number) => void;
  showAgentSidebar: (visible: boolean) => void;
  widget?: AgentWidgetType;
  showWidget(widget: AgentWidgetType): void;
  lastAttachments: UploadFile[];
  setLastAttachments: (files: UploadFile[]) => void;
}

export const AgentContext = React.createContext<AgentContextType>({
  agentAvailable: false,
  agentVisible: false,
  agentSidebarWidth: 0,
  setAgentSidebarWidth: () => {},
  showAgentSidebar: () => {},
  showWidget: () => {},
  lastAttachments: [],
  setLastAttachments: () => {},
});

export function useAgentContext() {
  return React.useContext(AgentContext);
}

export function AgentContextProvider({ children }: { children: ReactNode }) {
  const [widget, showWidget] = useState<AgentWidgetType>();
  const [agentVisible, showAgentSidebar] = useState(false);
  const { user, currentTenant } = useCurrentUser();

  const [agentSidebarWidth, setAgentSidebarWidth] = useState(0);
  const [lastAttachments, setLastAttachments] = useState<UploadFile[]>([]);

  useEffect(() => {
    setAgentSidebarWidth(agentVisible ? 400 : 0);
  }, [agentVisible]);

  const agentAvailable =
    !!user?.permissions?.settings?.agents && !!currentTenant.features.agents;

  return (
    <AgentContext.Provider
      value={{
        agentAvailable,
        agentVisible,
        agentSidebarWidth,
        setAgentSidebarWidth,
        showAgentSidebar,
        widget,
        showWidget,
        lastAttachments,
        setLastAttachments,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
}
